import { createSlice, PayloadAction, createSelector } from 'redux-starter-kit';

import { selectAppState } from '../../../App/state/selectors';
import { AppState } from '../../../App/state/reducer';
import { AppNotification } from '../types';

export type NotificationsState = {
  notificationHistory: AppNotification[];
  unreadNotifications: AppNotification[];
  fullHistoryLoaded: boolean;
  isLoadingHistory: boolean;
  unreadCount: number;
  isLoadingUnread: boolean;
  fullUnreadLoaded: boolean;
};

type Pagination = {
  limit: number;
  offset: number;
};

export type SetAction = PayloadAction<AppNotification[]>;
export type PushAction = PayloadAction<AppNotification[]>;
export type MarkAsReadAction = PayloadAction<string>;
export type AddToHistoryAction = PayloadAction<AppNotification[]>;
export type ShowAction = PayloadAction<AppNotification[]>;
export type UnreadCountAction = PayloadAction<number>;
export type UnreadNotificationsAction = PayloadAction<AppNotification[]>;
export type FetchHistoryAction = PayloadAction<Pagination>;
export type GetUnreadNotificationsAction = PayloadAction<Pagination>;

const initialState: NotificationsState = {
  notificationHistory: [],
  unreadNotifications: [],
  fullHistoryLoaded: false,
  isLoadingHistory: false,
  unreadCount: 0,
  isLoadingUnread: false,
  fullUnreadLoaded: false,
};

const notification = createSlice({
  slice: 'notifications',
  initialState: initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchHistory(state, action: FetchHistoryAction) {
      state.isLoadingHistory = true;
    },
    fetchHistorySuccess(state) {
      state.isLoadingHistory = false;
    },
    addToHistory(state, action: AddToHistoryAction) {
      action.payload.forEach(notification =>
        state.notificationHistory.push(notification),
      );
    },
    setFullHistoryLoaded(state) {
      state.fullHistoryLoaded = true;
    },
    getUnreadCount() {},
    setUnreadCount(state, action: UnreadCountAction) {
      state.unreadCount = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getUnreadNotifications(state, action: GetUnreadNotificationsAction) {
      state.isLoadingUnread = true;
    },
    getUnreadNotificationsSuccess(state, action: PushAction) {
      state.isLoadingUnread = false;
      action.payload.forEach(notification =>
        state.unreadNotifications.push(notification),
      );
    },
    setFullUnreadLoaded(state) {
      state.fullUnreadLoaded = true;
      state.isLoadingUnread = false;
    },
    resetHistory(state) {
      state.notificationHistory = [];
    },
    push(state, action: PushAction) {
      action.payload.forEach(notification =>
        state.unreadNotifications.unshift(notification),
      );
    },
    markAsRead(state, action: MarkAsReadAction) {
      const index = state.unreadNotifications.findIndex(
        notification => notification.id === action.payload,
      );
      if (index !== -1) {
        state.notificationHistory.unshift(state.unreadNotifications[index]);
        state.unreadNotifications.splice(index, 1);
        state.unreadCount--;
      }
    },
    markAllAsRead() {},
    markAllAsReadSuccess(state) {
      for (let i = state.unreadNotifications.length - 1; i >= 0; i--) {
        state.notificationHistory.unshift(state.unreadNotifications[i]);
      }
      state.unreadNotifications = [];
    },
  },
});

const { reducer } = notification;

const notificationActions = notification.actions;

const notificationSelectors = {
  getHistory: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.notificationHistory,
  ),
  getUnreadCount: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.unreadCount,
  ),
  getUnread: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.unreadNotifications,
  ),
  getFullHistoryLoaded: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.fullHistoryLoaded,
  ),
  getFullUnreadLoaded: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.fullUnreadLoaded,
  ),
  getIsLoadingUnread: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.isLoadingUnread,
  ),
  getIsLoadingHistory: createSelector(
    [selectAppState],
    (state: AppState) => state.notifications.isLoadingHistory,
  ),
};

export { reducer as default, notificationActions, notificationSelectors };
